import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, Money } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST } from "@/lib/format";

const MODES = [["cash", "Cash"], ["bank", "Bank"], ["upi", "UPI"]];

const EntryForm = ({ kind, categories, onSaved }) => {
  const blank = { date: todayIST(), amount: "", mode: "cash", category: kind === "expense" ? categories[0] || "Other" : "Other income", note: "" };
  const [f, setF] = useState(blank);
  const save = async () => {
    try { await api.post(`/accounts/${kind === "expense" ? "expenses" : "income"}`, { ...f, amount: +f.amount }); toast.success("Saved"); setF(blank); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-2 rounded-2xl border border-[#E7E2D6] bg-white p-4 sm:grid-cols-6" data-testid={`${kind}-form`}>
      <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`${kind}-date`} />
      {kind === "expense"
        ? <NativeSelect value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="expense-category">{categories.map((c) => <option key={c}>{c}</option>)}</NativeSelect>
        : <Input value="Other income" disabled />}
      <Input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount" data-testid={`${kind}-amount`} />
      <NativeSelect value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid={`${kind}-mode`}>{MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</NativeSelect>
      <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={kind === "expense" ? "Note (vendor, bill no.)" : "Source (e.g. scrap sale)"} data-testid={`${kind}-note`} />
      <Button disabled={!(+f.amount > 0)} onClick={save} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid={`${kind}-save`}>Add {kind}</Button>
    </div>
  );
};

export const ExpensesTab = ({ rows, categories, onChanged }) => {
  const remove = async (x) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/accounts/expenses/${x.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="expenses-tab">
      <EntryForm kind="expense" categories={categories} onSaved={onChanged} />
      <EntryForm kind="income" categories={categories} onSaved={onChanged} />
      {rows.length === 0 && <div className="rounded-2xl border border-dashed border-[#E7E2D6] p-8 text-center text-sm text-muted-foreground" data-testid="expenses-empty">No expenses this month</div>}
      {rows.map((x) => (
        <div key={x.id} className="flex items-center gap-3 rounded-xl border border-[#E7E2D6] bg-white px-4 py-2.5 text-sm" data-testid={`expense-${x.id}`}>
          <span className="w-24 font-mono text-xs text-muted-foreground">{x.date}</span>
          <span className="flex-1"><b>{x.category}</b>{x.note ? ` · ${x.note}` : ""} <span className="text-xs uppercase text-muted-foreground">{x.mode}</span></span>
          <Money value={x.amount} className="font-semibold" />
          {!x.ref && <Button size="icon" variant="ghost" onClick={() => remove(x)} aria-label="Delete expense" data-testid={`expense-delete-${x.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        </div>
      ))}
    </div>
  );
};
